const encodeSvg = (markup: string) =>
  `data:image/svg+xml;utf8,${encodeURIComponent(markup)}`;

const escapeText = (text: string) =>
  text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const FONT_STACK = "Noto Sans HK, Verdana, sans-serif";

type LocaleKey = "zh-Hant" | "en";

interface BadgeOptions {
  width?: number;
  height?: number;
  gradientStart?: string;
  gradientEnd?: string;
  radius?: number;
  fontSize?: number;
}

export const createBadgeGraphic = (
  label: string,
  options: BadgeOptions = {},
): string => {
  const {
    width = 240,
    height = 72,
    gradientStart = "#3B82F6",
    gradientEnd = "#22D3EE",
    radius = 20,
    fontSize = 24,
  } = options;

  const safeLabel = escapeText(label);

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" role="img" aria-label="${safeLabel}">
    <defs>
      <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="${gradientStart}"/>
        <stop offset="100%" stop-color="${gradientEnd}"/>
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="${width}" height="${height}" rx="${radius}" fill="url(#grad)"/>
    <text x="50%" y="55%" text-anchor="middle" font-family="${FONT_STACK}" font-size="${fontSize}" fill="#FFFFFF">${safeLabel}</text>
  </svg>`;

  return encodeSvg(svg);
};

export const createBannerGraphic = (
  label: string,
  locale: LocaleKey,
  position: "top" | "bottom",
): string => {
  const safeLabel = escapeText(label);
  const width = 720;
  const height = 220;
  const gradientStart = position === "top" ? "#F97316" : "#10B981";
  const gradientEnd = position === "top" ? "#FACC15" : "#22D3EE";
  const fontSize = locale === "en" ? 36 : 40;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" role="img" aria-label="${safeLabel}">
    <defs>
      <linearGradient id="banner-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="${gradientStart}"/>
        <stop offset="100%" stop-color="${gradientEnd}"/>
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="${width}" height="${height}" rx="32" fill="url(#banner-grad)"/>
    <text x="50%" y="54%" text-anchor="middle" font-family="${FONT_STACK}" font-size="${fontSize}" font-weight="600" fill="#FFFFFF">${safeLabel}</text>
  </svg>`;

  return encodeSvg(svg);
};

export const createStatusIcon = (
  type: "correct" | "incorrect",
): string => {
  const isCorrect = type === "correct";
  const color = isCorrect ? "#10B981" : "#EF4444";
  const path = isCorrect
    ? `<path d="M18 30 L28 40 L46 18" stroke="#FFFFFF" stroke-width="6" fill="none" stroke-linecap="round" stroke-linejoin="round"/>`
    : `<path d="M20 20 L44 44 M44 20 L20 44" stroke="#FFFFFF" stroke-width="6" stroke-linecap="round"/>`;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64" role="img" aria-label="${
    isCorrect ? "Correct" : "Incorrect"
  } icon">
    <circle cx="32" cy="32" r="30" fill="${color}"/>
    ${path}
  </svg>`;
  return encodeSvg(svg);
};

export const createModalGraphic = (
  type: "success" | "join",
  locale: LocaleKey,
): string => {
  const safeLocale = locale === "en" ? "EN" : "ZH";
  const label =
    type === "success"
      ? locale === "en"
        ? "Great Job!"
        : "表現出色！"
      : locale === "en"
        ? "Join the Quiz"
        : "立即參加";
  const colors =
    type === "success"
      ? ["#7C3AED", "#22D3EE"]
      : ["#F97316", "#FACC15"];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="640" height="360" viewBox="0 0 640 360" role="img" aria-label="${escapeText(label)} (${safeLocale})">
    <defs>
      <linearGradient id="modal-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="${colors[0]}"/>
        <stop offset="100%" stop-color="${colors[1]}"/>
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="640" height="360" rx="32" fill="url(#modal-grad)"/>
    <text x="50%" y="44%" text-anchor="middle" font-family="${FONT_STACK}" font-size="${
      locale === "en" ? 42 : 48
    }" font-weight="600" fill="#FFFFFF">${escapeText(label)}</text>
    <text x="50%" y="68%" text-anchor="middle" font-family="${FONT_STACK}" font-size="22" fill="#FFFFFF" opacity="0.8">${escapeText(
      type === "success"
        ? locale === "en"
          ? "Download your prize wallpaper now"
          : "立即下載專屬桌布"
        : locale === "en"
          ? "Secure your spot in the competition"
          : "把握機會加入比賽",
    )}</text>
  </svg>`;

  return encodeSvg(svg);
};

export const createWallpaperDataUri = (locale: LocaleKey): string => {
  const label = locale === "en" ? "CPCE 2025" : "CPCE 2025 智慧問答";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1920" height="1080" viewBox="0 0 1920 1080">
    <defs>
      <linearGradient id="wallpaper-grad" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stop-color="#0F172A"/>
        <stop offset="100%" stop-color="#1E3A8A"/>
      </linearGradient>
    </defs>
    <rect x="0" y="0" width="1920" height="1080" fill="url(#wallpaper-grad)"/>
    <text x="50%" y="48%" text-anchor="middle" font-family="${FONT_STACK}" font-size="${
      locale === "en" ? 128 : 112
    }" font-weight="600" fill="#38BDF8">${escapeText(label)}</text>
    <text x="50%" y="64%" text-anchor="middle" font-family="${FONT_STACK}" font-size="48" fill="#FACC15">${escapeText(
      locale === "en"
        ? "Quiz Championship"
        : "全城挑戰賽",
    )}</text>
  </svg>`;
  return encodeSvg(svg);
};

export type { LocaleKey };
